import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {Observable, catchError, from, switchMap, throwError} from 'rxjs';
import {environment} from '../../environments/environment';
import {normalizeBaseUrl} from '../shared/api/api-url';
import type {AuthSession} from './auth.models';
import {AuthError, AuthService} from './auth.service';

function shouldSkipAuthHeader(url: string): boolean {
  const apiBaseUrl = normalizeBaseUrl(environment.apiBaseUrl ?? '');
  if (!apiBaseUrl || (url !== apiBaseUrl && !url.startsWith(`${apiBaseUrl}/`))) {
    return true;
  }

  return url.includes('/v1/auth/login')
    || url.includes('/v1/auth/register')
    || url.includes('/v1/auth/verify-email')
    || url.includes('/v1/auth/forgot-password')
    || url.includes('/v1/auth/reset-password')
    || url.includes('/v1/auth/google')
    || url.includes('/v1/auth/refresh')
    || url.includes('/v1/auth/logout')
    || url.includes('/v1/auth/google-diagnostics')
    || url.includes('/v1/pricing-plans')
    || /\/v1\/releases(?:\?|$)/.test(url);
}

const AUTH_RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (shouldSkipAuthHeader(request.url)) {
      return next.handle(request);
    }

    const authService = this.injector.get(AuthService);
    if (!authService.session) {
      return next.handle(request);
    }

    if (authService.shouldRefreshAccessToken()) {
      return from(authService.refreshSession()).pipe(
        switchMap((session) => this.handleAuthenticated(request, session, next)),
        catchError((error: unknown) => {
          if (error instanceof AuthError && error.status === 429 && authService.session) {
            return this.handleAuthenticated(request, authService.session, next);
          }

          return throwError(() => error);
        }),
      );
    }

    return this.handleAuthenticated(request, authService.session, next);
  }

  private handleAuthenticated(
    request: HttpRequest<unknown>,
    session: AuthSession,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const authenticatedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${session.accessToken}`,
      },
    });

    return next.handle(authenticatedRequest).pipe(
      catchError((error: unknown) => {
        if (!(error instanceof HttpErrorResponse)
          || error.status !== 401
          || request.context.get(AUTH_RETRY_AFTER_REFRESH)) {
          return throwError(() => error);
        }

        const authService = this.injector.get(AuthService);
        return from(authService.refreshSession()).pipe(
          switchMap((refreshedSession) => next.handle(request.clone({
            context: request.context.set(AUTH_RETRY_AFTER_REFRESH, true),
            setHeaders: {
              Authorization: `Bearer ${refreshedSession.accessToken}`,
            },
          }))),
        );
      }),
    );
  }
}
